import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-medium rounded ring-offset-1 transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 shadow-sm-soft',
        destructive: 'bg-danger text-white hover:bg-red-700',
        outline:
          'border border-neutral-200 bg-white text-neutral-800 hover:bg-neutral-50 hover:border-neutral-300',
        'outline-solid':
          'border border-neutral-200 bg-white text-neutral-800 hover:bg-neutral-50 hover:border-neutral-300',
        secondary: 'bg-neutral-100 text-neutral-800 hover:bg-neutral-150 active:bg-neutral-200',
        ghost: 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900',
        link: 'text-primary-600 hover:text-primary-700 underline-offset-4 hover:underline px-0',
      },
      size: {
        default: 'h-8 px-3 text-sm gap-2 has-[>svg]:px-3',
        xs: 'h-6 px-2 text-[11px] gap-1 has-[>svg]:px-2',
        sm: 'h-7 px-2.5 text-xs gap-1.5 has-[>svg]:px-2.5',
        lg: 'h-10 px-4 text-sm gap-2 has-[>svg]:px-4',
        icon: 'h-8 w-8 p-0',
        'icon-sm': 'h-7 w-7 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
